import { ClientContext } from '@/app/utils/clientContext';
import { InverterAction } from '@/app/types/global';

export type SupportDiagnosticSnapshot = {
  captured_at: string;
  url: string;
  telemetry_freshness: string | null;
  recent_commands: Array<Record<string, unknown>>;
  recent_errors: Array<Record<string, unknown>>;
  browser: ClientContext;
  screenshot_attached: boolean;
  reporter_preferences: {
    in_app_updates: true;
    email_copy: boolean;
  };
};

const ERROR_KEYS = ['error', 'errors', 'api_error', 'api_errors', 'failed', 'exception'];

/**
 * Allowlist the small action slice support needs. Raw values/action_params can
 * contain credentials, network addresses or long rule traces and must not be
 * copied into the case context wholesale.
 */
export function safeActionSnapshot(action: Partial<InverterAction>): Record<string, unknown> {
  return {
    id: action.id ?? null,
    inverter_id: action.inverter_id ?? null,
    time: action.time ?? null,
    action: action.action ?? null,
    reason: action.reason ?? null,
    quality: action.quality ?? null,
    start_soc: action.start_soc ?? null,
    battery_power: action.pbattery ?? null,
    grid_energy: action.e_grid ?? null,
  };
}

function actionTimestamp(action: Partial<InverterAction>): number | null {
  if (!action.time) return null;
  const parsed = new Date(action.time).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

/**
 * The first action is the interval the reporter selected. Keep it first, then
 * add the closest surrounding commands instead of whichever rows happened to
 * appear first in the chart's source order.
 */
export function supportActionWindow(
  actions: Partial<InverterAction>[],
  limit = 5,
): Partial<InverterAction>[] {
  if (limit <= 0 || actions.length === 0) return [];
  const [selected, ...remaining] = actions;
  const selectedAt = actionTimestamp(selected);
  const ranked = remaining
    .map((action, index) => ({ action, index, at: actionTimestamp(action) }))
    .sort((left, right) => {
      if (selectedAt !== null) {
        const leftDistance = left.at === null ? Number.POSITIVE_INFINITY : Math.abs(left.at - selectedAt);
        const rightDistance = right.at === null ? Number.POSITIVE_INFINITY : Math.abs(right.at - selectedAt);
        if (leftDistance !== rightDistance) return leftDistance - rightDistance;
      } else if (left.at !== right.at) {
        if (left.at === null) return 1;
        if (right.at === null) return -1;
        return right.at - left.at;
      }
      return left.index - right.index;
    });
  return [selected, ...ranked.map(({ action }) => action)].slice(0, limit);
}

function actionErrors(action: Partial<InverterAction>): Record<string, unknown> | null {
  const row: Record<string, unknown> = { id: action.id ?? null, time: action.time ?? null };
  let hasError = false;
  const source = action as unknown as Record<string, unknown>;
  for (const key of ERROR_KEYS) {
    const value = source[key];
    if (value !== null && value !== undefined && value !== '' && value !== false) {
      row[key] = value;
      hasError = true;
    }
  }
  return hasError ? row : null;
}

export function buildSupportDiagnosticSnapshot({
  actions,
  browser,
  url,
  screenshotAttached = false,
  emailCopy = false,
}: {
  actions: Partial<InverterAction>[];
  browser: ClientContext;
  url: string;
  screenshotAttached?: boolean;
  emailCopy?: boolean;
}): SupportDiagnosticSnapshot {
  const recent = supportActionWindow(actions, 5);
  return {
    captured_at: new Date().toISOString(),
    url,
    telemetry_freshness: recent[0]?.time?.toString() ?? null,
    recent_commands: recent.map(safeActionSnapshot),
    recent_errors: recent.map(actionErrors).filter((row): row is Record<string, unknown> => !!row),
    browser,
    screenshot_attached: screenshotAttached,
    reporter_preferences: { in_app_updates: true, email_copy: emailCopy },
  };
}
